import { createReducer, on } from '@ngrx/store';

import * as fromPersistedStateActions from '../actions/persisted-state.actions';
import * as fromPropertyProfileActions from '../actions/property-profile.actions';

export const propertyProfileFeatureKey = 'propertyProfile';

import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';

import { filter, find, forEach, isUndefined } from 'lodash-es';

import { defaultPropertyProfileExportOptions } from '../../constants/profile.constant';
import * as fromWalkingFarmActions from '../actions/walking-farm.actions';
import { PropertyProfileHistoryItem, StoredPropertyProfile } from '../models/property-profile.model';

export interface State extends EntityState<StoredPropertyProfile> {
  history: PropertyProfileHistoryItem[];
  mapViewDefaultCenterLocation: google.maps.LatLngLiteral;
  propertySearchAddressComponents: google.maps.GeocoderAddressComponent[] | null;
  propertySearchAddressLocation: google.maps.LatLngLiteral | null;
  propertySearchKey: string | null;
  propertySearchText: string | null;
  selectedPropertyProfileId: string | null;
}

export function selectPropertyProfileId(entity: StoredPropertyProfile) {
  return entity.reportId;
}

export const adapter: EntityAdapter<StoredPropertyProfile> = createEntityAdapter<StoredPropertyProfile>({
  selectId: selectPropertyProfileId
});

const defaultCenterLocation: google.maps.LatLngLiteral = { lat: 33.685909, lng: -117.824722 };

export const initialState: State = adapter.getInitialState({
  history: [],
  mapViewDefaultCenterLocation: defaultCenterLocation,
  propertySearchAddressComponents: null,
  propertySearchAddressLocation: null,
  propertySearchKey: null,
  propertySearchText: null,
  selectedPropertyProfileId: null
});

export const reducer = createReducer(
  initialState,
  on(fromPropertyProfileActions.setPropertyProfile, (state, { payload: { storedPropertyProfile } }) =>
    adapter.setOne(storedPropertyProfile, state)
  ),
  on(fromPropertyProfileActions.setSelectPropertyProfile, (state, { payload: { storedPropertyProfile } }) => ({
    ...state,
    selectedPropertyProfileId: selectPropertyProfileId(storedPropertyProfile)
  })),
  on(fromPropertyProfileActions.geocodeLocationSuccess, (state, { payload }) => ({
    ...state,
    propertySearchAddressComponents: payload.result.address_components,
    propertySearchAddressLocation: payload.result.geometry.location.toJSON()
  })),
  on(fromPropertyProfileActions.updatePropertySearchCenterLocation, (state, { payload }) => ({
    ...state,
    propertySearchCenterLocation: payload.location
  })),
  on(fromPropertyProfileActions.updatePropertySearchLocation, (state, { payload }) => ({
    ...state,
    propertySearchAddressLocation: payload.location,
    propertySearchText: payload.address
  })),
  on(fromPropertyProfileActions.resetSearch, fromWalkingFarmActions.geocodeAddressLocation, (state) => ({
    ...state,
    propertySearchAddressComponents: initialState.propertySearchAddressComponents,
    propertySearchAddressLocation: initialState.propertySearchAddressLocation,
    propertySearchText: initialState.propertySearchText
  })),
  on(fromPropertyProfileActions.loadProfileHistorySuccess, (state, { payload }) => {
    const missingPropertyIds: string[] = [];
    forEach(state.ids, (propertyId) => {
      const property = find(payload, (item) => item.id === propertyId);

      if (!property) {
        missingPropertyIds.push(`${propertyId}`);
      }
    });

    return adapter.removeMany(missingPropertyIds, {
      ...state,
      history: payload
    });
  }),
  on(fromPropertyProfileActions.deleteProfileHistoryItemSuccess, (state, { payload }) => {
    const newState = { ...state };
    newState.history = filter(newState.history, (property) => property.id !== payload.id);
    return adapter.removeOne(payload.id, newState);
  }),
  on(fromPropertyProfileActions.refreshKey, (state) => ({
    ...state,
    propertySearchKey: null
  })),
  on(fromPropertyProfileActions.refreshKeySuccess, (state, { payload }) => ({
    ...state,
    propertySearchKey: payload.key
  })),
  on(fromPropertyProfileActions.resetKey, (state) => ({
    ...state,
    propertySearchKey: initialState.propertySearchKey
  })),
  on(fromPersistedStateActions.loadPersistedStateSuccess, (state, { payload }) => ({
    ...state,
    ...payload[propertyProfileFeatureKey]
  })),
  on(fromPersistedStateActions.persistedStateReset, fromPersistedStateActions.loadPersistedStateFailure, () => ({
    ...initialState
  })),
  on(fromPropertyProfileActions.loadReportHtml, (state, { payload }) => {
    const propertyProfile = selectEntities(state)[payload.reportId];
    return propertyProfile
      ? adapter.updateOne(
          {
            changes: {
              reportExportPreviewHtml: null
            },
            id: payload.reportId
          },
          { ...state }
        )
      : state;
  }),
  on(fromPropertyProfileActions.loadReportHtmlSuccess, (state, { payload }) => {
    const propertyProfile = selectEntities(state)[payload.reportId];
    return propertyProfile
      ? adapter.updateOne(
          {
            changes: {
              reportExportPreviewHtml: payload.reportHtml
            },
            id: payload.reportId
          },
          { ...state }
        )
      : state;
  }),
  on(
    fromPropertyProfileActions.updateReportExportOptions,
    (
      state,
      {
        payload: {
          isReportExportDetailsAndCharacteristicsVisible,
          isReportExportPlatMapVisible,
          isReportExportSaleInformationVisible,
          isReportExportSchoolsVisible,
          isReportExportTransactionHistoryVisible,
          reportId
        }
      }
    ) => {
      const propertyProfile = selectEntities(state)[reportId];
      return propertyProfile
        ? adapter.updateOne(
            {
              changes: {
                ...(!isUndefined(isReportExportDetailsAndCharacteristicsVisible) && {
                  isReportExportDetailsAndCharacteristicsVisible
                }),
                ...(!isUndefined(isReportExportPlatMapVisible) && {
                  isReportExportPlatMapVisible
                }),
                ...(!isUndefined(isReportExportSaleInformationVisible) && {
                  isReportExportSaleInformationVisible
                }),
                ...(!isUndefined(isReportExportSchoolsVisible) && {
                  isReportExportSchoolsVisible
                }),
                ...(!isUndefined(isReportExportTransactionHistoryVisible) && {
                  isReportExportTransactionHistoryVisible
                })
              },
              id: reportId
            },
            { ...state }
          )
        : state;
    }
  ),
  on(fromPropertyProfileActions.resetReportOptions, (state, { payload }) => {
    const propertyProfile = selectEntities(state)[payload.reportId];
    return propertyProfile
      ? adapter.updateOne(
          {
            changes: {
              ...defaultPropertyProfileExportOptions
            },
            id: payload.reportId
          },
          { ...state }
        )
      : state;
  })
);

const { selectEntities } = adapter.getSelectors();

export const propertyProfileSelectEntities = selectEntities;
